<template>
    <modal
        :title="$t('dosages.form.dosageDefinition')"
        size="sm"
        :isOpen="isOpen"
        @update:isOpen="$emit('update:isOpen', $event)"
        @submit="handleSaveAndClose"
    >
        <template v-slot:default v-if="requestData.selectedDrugModel">
            <div class="form-group">
                <label>{{ $t('dosages.form.route') }}</label>
                <select v-model="selectedRouteId">
                    <option
                        v-for="routeEntry in requestData.selectedDrugModel.model.formulationAndRoutes"
                        :key="routeEntry[0]"
                        :value="routeEntry[0]"
                    >
                        {{ $t(`administrationRoute.${routeEntry[1].administrationRoute}`) }}
                    </option>
                </select>
            </div>

            <generic-dosage-choice-subform
                v-if="selectedFormulationAndRoute"
                v-model="dosage"
                :formulation-and-route="selectedFormulationAndRoute"
            />

            <div class="form-group">
                <div class="input-checkbox inline">
                    <input type="checkbox" id="dosageIsSteady" v-model="isSteadyState" :disabled="!isSteadyStateAllowed" />
                    <label for="dosageIsSteady">{{ $t('dosages.form.isSteady') }}</label>
                </div>
            </div>

            <div v-if="isSteadyState">
                <div class="form-group">
                    <label>{{ $t('dosages.form.lastDose') }}</label>
                    <date-picker v-model="lastDoseDate" with-time required />
                </div>
            </div>

            <div v-else>
                <div class="form-group">
                    <label>{{ $t('dosages.form.from') }}</label>
                    <date-picker v-model="startDate" with-time required />
                </div>
                <div class="form-group">
                    <label>{{ $t('dosages.form.to') }}</label>
                    <date-picker v-model="endDate" with-time required />
                </div>
            </div>
        </template>

        <template v-slot:footer>
            <button type="submit" class="btn">
                {{ $t('action.ok') }}
            </button>
            <button type="button" class="btn" @click="handleSave">
                {{ $t('action.apply') }}
            </button>
            <button type="button" class="btn" @click="handleClose">
                {{ $t('action.cancel') }}
            </button>
        </template>
    </modal>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, watch } from 'vue';
import DatePicker from '@/components/DatePicker.vue';
import Modal from '@/components/Modal.vue';
import { FormulationAndRouteFull } from '@/service/models/response/drug-model-detail/formulation-and-route-full';
import { DosageTimeRange } from '@/service/models/request/computation/dosage-time-range';
import { DosageSteadyState } from '@/service/models/common/query-dosage/parts/dosage-steady-state';
import { QueryDosage } from '@/service/models/common/query-dosage/query-dosage';
import { BaseDosageChoice } from '@/service/models/common/query-dosage/dosage-choices';
import { DosageLoop } from '@/service/models/common/query-dosage/parts/dosage-loop';
import { LastingDosageVM } from '@/service/view-models/dosage-choices/lasting-dosage-vm';
import { BaseDosageChoiceVM } from '@/service/view-models/dosage-choices/base-dosage-choice-vm';
import { useRequestDataStore } from '@/store/request-data';
import GenericDosageChoiceSubform from '../common/dosage-choices-subforms/GenericDosageChoiceSubform.vue';

export default defineComponent({
    components: {
        DatePicker,
        Modal,
        GenericDosageChoiceSubform,
    },

    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },

        index: {
            type: Number,
            required: true,
        },

        data: {
            type: null as unknown as PropType<DosageTimeRange|null>,
            required: true,
        },

        isSteadyStateAllowed: {
            type: Boolean,
            required: true,
        },
    },

    emits: {
        "update:isOpen": (payload: boolean) => typeof payload === "boolean",
        "save": (payload: {index: number, data: DosageTimeRange}) => typeof payload === "object",
    },

    setup(props, {emit})
    {
        const requestData = useRequestDataStore();

        const lastDoseDate = ref<Date>(new Date());
        const startDate = ref<Date>(lastDoseDate.value);
        const endDate = ref<Date|null>(null);
        const isSteadyState = ref(false);
        const dosage = ref<BaseDosageChoiceVM|null>(null);

        function refreshInternalData(newData: DosageTimeRange|null)
        {
            if (newData === null) {
                // Create a new dosage. For now, only LastingDosage is supported
                lastDoseDate.value = new Date();
                startDate.value = lastDoseDate.value;
                endDate.value = null;
                isSteadyState.value = props.isSteadyStateAllowed;
                dosage.value = new LastingDosageVM();
                return;
            }

            // Fill dosage with model data
            endDate.value = newData.end;
            dosage.value = newData.dosage.getInnerDosageChoice().makeViewModel();

            if (newData.dosage.dosageRepeat instanceof DosageSteadyState) {
                isSteadyState.value = true;
                lastDoseDate.value = newData.dosage.dosageRepeat.lastDoseDate;
                startDate.value = lastDoseDate.value;
            }
            else {
                isSteadyState.value = false;
                startDate.value = newData.start;
                lastDoseDate.value = newData.start;
            }
        }

        // The internal data object needs to be copied either when the parent component changes it
        // or each time the modal is re-opened (for example to create multiple new items).
        refreshInternalData(props.data);
        watch(
            [() => props.data, () => props.isOpen],
            () => refreshInternalData(props.data)
        );

        // Sync the start date and the last dose date
        watch(lastDoseDate, (newValue) => {
            startDate.value = newValue;
        });
        watch(startDate, (newValue) => {
            lastDoseDate.value = newValue;
        });

        const selectedRouteId = ref(requestData.selectedDrugModel?.model.defaultFormulationAndRoute.id ?? "");

        const selectedFormulationAndRoute = computed<FormulationAndRouteFull|null>(() => {
            return requestData.selectedDrugModel?.model.getFormulationAndRoute(selectedRouteId.value) ?? null;
        });

        function buildQueryDosage(dosage: BaseDosageChoice, isSteadyState: boolean, lastDoseDate: Date): QueryDosage
        {
            if (!isSteadyState) {
                return new QueryDosage(new DosageLoop(dosage));
            }

            return new QueryDosage(new DosageSteadyState(lastDoseDate, dosage));
        }

        function handleSave(e: Event)
        {
            const button = e.target as HTMLButtonElement|null;

            // Run the form validation. This will already be done if called form the submit button,
            // but not if called from the "Apply" button.
            if (button?.form) {
                if (!button.form.reportValidity()) {
                    return;
                }
            }

            let tmpStartDate = startDate.value;
            let tmpEndDate = endDate.value;

            // Start and end dates should be considered in a special way when dealing with a steady state
            if (isSteadyState.value) {
                tmpStartDate = new Date(lastDoseDate.value.getTime());
                tmpEndDate = new Date(lastDoseDate.value.getTime());
                tmpStartDate.setFullYear(tmpStartDate.getFullYear() - 1);
                tmpEndDate.setFullYear(tmpEndDate.getFullYear() + 1);
            }

            // The form validation should prevent this to happen, but just to ensure type
            // safety, check all the required data again here.
            if (tmpEndDate === null || dosage.value === null || !dosage.value.isValid()) {
                return;
            }

            // Build up a model object to return
            const dosageTimeRange = new DosageTimeRange(
                tmpStartDate,
                tmpEndDate,
                buildQueryDosage(dosage.value.getModel(), isSteadyState.value, lastDoseDate.value)
            );

            emit("save", {
                index: props.index,
                data: dosageTimeRange,
            });
        }

        function handleClose()
        {
            emit("update:isOpen", false);
        }

        function handleSaveAndClose(e: Event)
        {
            handleSave(e);
            handleClose();
        }

        return {
            requestData,
            selectedRouteId,
            selectedFormulationAndRoute,
            handleSave,
            handleClose,
            handleSaveAndClose,

            startDate,
            endDate,
            lastDoseDate,
            isSteadyState,
            dosage,
        };
    }
});
</script>
